/**
 * Verification.jsx — the badge, the tooltip, and the recurring prompt.
 *
 * WHY THE BADGE IS WORDED THE WAY IT IS
 *
 * "Unverified" on its own reads as an accusation. The tooltip therefore says
 * what is actually true — *we* have not checked, this person has not
 * necessarily done anything wrong — and offers the way out. That framing
 * matters because most unverified users will be legitimate clinicians who
 * simply have not got round to it.
 *
 * WHY THE JOB TITLE CHANGES TOO
 *
 * The badge alone is not enough. If an unverified account still renders
 * "Consultant Cardiologist" in the app's own styling, the interface is still
 * asserting a credential it has not checked. For unverified accounts the
 * credential line is explicitly marked self-reported, which moves it from
 * something Clincordant says to something the user says.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, GraduationCap, HelpCircle, ShieldCheck, X, Clock } from 'lucide-react';

const STORAGE_KEY = 'leeflet.verifyPromptDismissedAt';
const REPROMPT_DAYS = 3;

/* ── Badge ─────────────────────────────────────────────────────────────────── */

const TIER_CONFIG = {
  clinician: {
    label: 'Verified',
    icon: BadgeCheck,
    className: 'vbadge vbadge--clinician',
    // No explanation box: the tick says it. null means the badge renders as
    // a plain label with nothing to open.
    tooltip: null,
  },
  student: {
    label: 'Student',
    icon: GraduationCap,
    className: 'vbadge vbadge--student',
    tooltip:
      'Clincordant has confirmed this person is a healthcare student through their university email address.',
  },
  unverified: {
    label: 'Unverified',
    icon: HelpCircle,
    className: 'vbadge vbadge--unverified',
    // Says what is factually true and stops there. An added instruction to
    // "treat this as an unverified opinion" reads as the platform passing
    // judgement on the person, when most unverified users are legitimate
    // clinicians who simply have not got round to it. State the fact; let the
    // reader draw the conclusion.
    tooltip:
      'This person has not registered as a clinician with us, so we have not checked their stated role or specialty.',
  },
};

/**
 * @param {'clinician'|'student'|'unverified'} tier
 * @param {boolean} [isSelf] — adds a "Get verified" link when it is your badge
 */
/**
 * @param gold — the Plus treatment of the same badge.
 *
 * Deliberately a modifier on the existing badge rather than a fourth tier.
 * Plus is a subscription, not a credential: it says nothing about whether this
 * person is a clinician, and a reader weighing a clinical claim must not be
 * able to mistake "paid" for "checked". So the icon, the label and the tooltip
 * are identical to the tier underneath — only the colour changes.
 */
export function VerifiedBadge({ tier = 'unverified', isSelf = false, size = 'sm', gold = false }) {
  const [open, setOpen] = useState(false);
  const base = TIER_CONFIG[tier] ?? TIER_CONFIG.unverified;
  // Gold is only ever applied over a real verification. An unverified account
  // with a subscription gets the ordinary unverified badge.
  const goldish = gold && (tier === 'clinician' || tier === 'student');
  const config = goldish
    ? { ...base, className: `${base.className} vbadge--gold` }
    : base;
  const Icon = config.icon;

  // Click-and-focus, not hover only. A hover tooltip is unreachable on a phone,
  // and this one carries information a reader needs to weigh a clinical claim.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!e.target.closest?.('.vbadge-wrap')) setOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  /**
   * Hover lives on the WRAPPER, not on the button, and closing is delayed.
   *
   * With the handlers on the button, moving the pointer towards "Get verified"
   * left the button — which closed the tooltip out from under the cursor before
   * it could ever be clicked. The link was unreachable by mouse entirely.
   *
   * The wrapper contains both the badge and the tooltip, so travelling between
   * them never leaves the hover region, and the 180ms delay covers the sub-pixel
   * gap where the pointer is technically over neither.
   */
  const closeTimer = useRef(null);

  const openNow = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);

  const closeSoon = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // A badge with nothing to explain is a plain label: no button, no hover box.
  if (!config.tooltip) {
    return (
      <span className="vbadge-wrap">
        <span className={`${config.className} vbadge--${size} vbadge--static`}>
          <Icon size={size === 'sm' ? 12 : 14} aria-hidden="true" />
          <span>{config.label}</span>
        </span>
      </span>
    );
  }

  return (
    <span
      className="vbadge-wrap"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        className={`${config.className} vbadge--${size}`}
        aria-label={`${config.label}. Tap for what this means.`}
        aria-expanded={open}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        onFocus={openNow}
        // Keyboard focus moving into the tooltip's own link must not close it
        // either, so the same delay applies.
        onBlur={closeSoon}
      >
        <Icon size={size === 'sm' ? 12 : 14} aria-hidden="true" />
        <span>{config.label}</span>
      </button>

      {open && (
        <span role="tooltip" className="vbadge__tip">
          {config.tooltip}
          {isSelf && tier === 'unverified' && (
            <Link
              to="/settings/verification"
              className="vbadge__tip-link"
              onFocus={openNow}
              onBlur={closeSoon}
            >
              Get verified →
            </Link>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * The credential line under a name.
 *
 * Verified:   "Cardiology · Consultant Geriatrician · Manchester"
 * Unverified: "Manchester" — location only.
 *
 * The role and specialty are stripped at publish time (see authorSnapshot in
 * usePosts.js) and rejected by the security rules if a client sends them
 * anyway, so by the time a post reaches this component the fields are already
 * empty for unverified authors. The guard here is belt-and-braces for historic
 * posts written before this rule existed.
 */
export function CredentialLine({ post, tier }) {
  const verified = tier === 'clinician' || tier === 'student';

  /**
   * Role and location. Never specialty.
   *
   * authorSnapshot stopped publishing it, but historic posts still carry the
   * field, so leaving the render in place would keep showing it on everything
   * written before that change. Specialty is a briefing input, not a
   * credential — it must not appear beside anyone's name anywhere.
   */
  // Location is no longer shown on posts at all — including posts written
  // before it stopped being published, which still carry the field.
  const parts = [
    verified && post.showJobTitle !== false && post.authorJobTitle,
  ].filter(Boolean);

  if (!parts.length) return null;

  return (
    <p className={`post__credentials${verified ? '' : ' post__credentials--unverified'}`}>
      {parts.join(' · ')}
    </p>
  );
}

/* ── Recurring prompt ──────────────────────────────────────────────────────── */

function daysSince(iso) {
  if (!iso) return Infinity;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return Infinity;
  return (Date.now() - then) / 86400000;
}

/**
 * Shown at the top of the social feed for unverified users.
 *
 * Dismissal is remembered for three days, then it comes back. That cadence is
 * a judgement call: often enough that people who mean to verify eventually do,
 * rare enough that it does not become wallpaper people stop seeing. It never
 * appears for pending or verified accounts, and never blocks the feed.
 */
export function VerificationBanner({ profile }) {
  const [dismissed, setDismissed] = useState(true);   // assume hidden until checked

  const tier = profile?.verificationTier ?? 'unverified';
  const status = profile?.verificationStatus ?? 'unverified';

  useEffect(() => {
    if (tier !== 'unverified' || status === 'pending') return;
    try {
      setDismissed(daysSince(localStorage.getItem(STORAGE_KEY)) < REPROMPT_DAYS);
    } catch {
      // Storage blocked (private mode, locked-down hospital browser).
      // Show it — an occasional extra prompt beats never showing it at all.
      setDismissed(false);
    }
  }, [tier, status]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch { /* nothing to do */ }
  }, []);

  // Pending review gets its own, quieter message — nagging someone who has
  // already submitted is the fastest way to make them resent the prompt.
  if (status === 'pending') {
    return (
      <div className="verify-banner verify-banner--pending" role="status">
        <Clock size={18} aria-hidden="true" />
        <div className="verify-banner__body">
          <strong>Verification under review</strong>
          <p>
            We are checking your documents. This usually takes under 48 hours, and
            your evidence is deleted as soon as we decide.
          </p>
        </div>
      </div>
    );
  }

  if (tier !== 'unverified' || dismissed) return null;

  return (
    <div className="verify-banner" role="region" aria-label="Verify your clinical role">
      <ShieldCheck size={20} aria-hidden="true" className="verify-banner__icon" />

      <div className="verify-banner__body">
        <strong>Verify your clinical role</strong>
        <p>
          Right now your posts are marked <em>Unverified</em> and your job title shows
          as self-reported. Verifying takes about 30 seconds with a work or university
          email address.
        </p>
      </div>

      <div className="verify-banner__actions">
        <Link to="/settings/verification" className="btn btn--primary btn--sm">
          Verify me
        </Link>
        <button
          type="button"
          className="icon-btn"
          onClick={dismiss}
          aria-label="Dismiss for now. This will reappear in a few days."
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/** Compact nudge for the profile page and settings. */
export function VerificationCallout({ profile }) {
  const tier = profile?.verificationTier ?? 'unverified';
  const status = profile?.verificationStatus ?? 'unverified';

  if (tier === 'clinician') {
    return (
      <div className="verify-callout verify-callout--done">
        <BadgeCheck size={16} aria-hidden="true" />
        <span>Verified clinician{profile?.verificationMethod === 'email' ? ' via institutional email' : ''}.</span>
      </div>
    );
  }
  if (tier === 'student') {
    return (
      <div className="verify-callout verify-callout--done">
        <GraduationCap size={16} aria-hidden="true" />
        <span>
          Verified student. <Link to="/settings/verification">Upgrade to clinician</Link> once
          you are on a professional register.
        </span>
      </div>
    );
  }
  if (status === 'pending') {
    return (
      <div className="verify-callout">
        <Clock size={16} aria-hidden="true" />
        <span>Verification under review — usually under 48 hours.</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="verify-callout verify-callout--warn">
        <HelpCircle size={16} aria-hidden="true" />
        <span>
          Your last verification was not approved.{' '}
          <Link to="/settings/verification">See why and try again</Link>.
        </span>
      </div>
    );
  }
  return (
    <div className="verify-callout">
      <ShieldCheck size={16} aria-hidden="true" />
      <span>
        You are not verified. <Link to="/settings/verification">Verify your clinical role</Link> to
        remove the Unverified label from your posts.
      </span>
    </div>
  );
}

export const VERIFICATION_CSS = `
.vbadge-wrap { position: relative; display: inline-flex; }

.vbadge {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px;
  border: 1px solid transparent;
  border-radius: var(--radius-full);
  font-family: var(--font-body);
  font-size: 11px; font-weight: 600; line-height: 1.4;
  cursor: help;
  white-space: nowrap;
  /* Expands the hit target to 44px without changing the visual chip size. */
  position: relative;
}
.vbadge::before { content: ''; position: absolute; inset: -11px; }
.vbadge--md { font-size: var(--text-xs); padding: 3px 9px; }

.vbadge--clinician {
  background: var(--primary-tint);
  color: var(--primary-text);
  border-color: color-mix(in srgb, var(--primary) 30%, transparent);
}
.vbadge--student {
  background: color-mix(in srgb, var(--info) 12%, transparent);
  color: var(--info);
  border-color: color-mix(in srgb, var(--info) 30%, transparent);
}
/**
 * The Plus treatment. Layered over a tier, never instead of one.
 *
 * Fixed gold rather than the accent colour, for the same reason the NEW flag
 * is: the accent changes with the reader's own theme choice, and a badge that
 * turns plum or slate stops meaning anything. These are the --new-* tokens,
 * which are already contrast-checked in both modes.
 */
.vbadge--static { cursor: default; }

.vbadge--gold {
  background: var(--new-bg);
  color: var(--new-ink);
  border-color: var(--new-line);
}

/* Muted, not alarming. Most unverified users are legitimate clinicians who
   have simply not got round to it, and a red badge would read as an accusation. */
.vbadge--unverified {
  background: var(--bg-alt);
  color: var(--ink-faint);
  border-color: var(--line);
}

/* The 8px gap below the tooltip is dead space the pointer has to cross to
   reach "Get verified". This invisible bridge makes it hoverable, so the
   journey never leaves the wrapper at all — the close delay is the backstop,
   not the mechanism. */
.vbadge__tip::after {
  content: '';
  position: absolute;
  top: 100%; left: 0; right: 0;
  height: 10px;
}

.vbadge__tip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-dropdown);
  width: max-content;
  max-width: min(300px, 78vw);
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: var(--ink);
  color: var(--bg);
  font-size: var(--text-xs);
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  white-space: normal;
  box-shadow: var(--shadow-md);
}
.vbadge__tip-link {
  display: block; margin-top: 8px;
  color: var(--bg); font-weight: 700; text-decoration: underline;
}

.post__credentials--unverified { color: var(--ink-faint); }
.post__self-reported { font-style: italic; opacity: 0.85; }

/* ── Banner ────────────────────────────────────────────────────────────────── */

.verify-banner {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px; margin-bottom: 14px;
  background: var(--primary-tint);
  border: 1px solid color-mix(in srgb, var(--primary) 25%, transparent);
  border-radius: var(--radius-lg);
}
.verify-banner--pending { background: var(--warning-bg); border-color: color-mix(in srgb, var(--warning) 30%, transparent); }
.verify-banner__icon { color: var(--primary-text); flex-shrink: 0; margin-top: 1px; }
.verify-banner__body { flex: 1; min-width: 0; font-size: var(--text-sm); }
.verify-banner__body strong { display: block; margin-bottom: 3px; }
.verify-banner__body p { margin: 0; color: var(--ink-soft); line-height: 1.5; }
.verify-banner__actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }

@media (max-width: 560px) {
  .verify-banner { flex-wrap: wrap; }
  .verify-banner__actions { width: 100%; justify-content: space-between; margin-top: 6px; }
}

.verify-callout {
  display: flex; align-items: flex-start; gap: 9px;
  padding: 11px 14px; margin: 12px 0;
  background: var(--bg-alt);
  border-radius: var(--radius);
  font-size: var(--text-sm); line-height: 1.5;
  color: var(--ink-soft);
  border: 1px solid var(--line-strong);
}
.verify-callout svg { flex-shrink: 0; margin-top: 2px; }
.verify-callout--done { background: var(--primary-tint); color: var(--primary-text); border-color: color-mix(in srgb, var(--primary) 30%, var(--line-strong)); }
.verify-callout--warn { background: var(--warning-bg); color: var(--ink); border-color: color-mix(in srgb, var(--warning) 40%, var(--line-strong)); }
.verify-callout a { color: inherit; font-weight: 600; }
`;
